import React, { useState, useEffect } from 'react';
import { Text, Flex, Box } from '@chakra-ui/react';
import ReactECharts from 'echarts-for-react';
import ChartCard from './ChartCard';
import { analyticsService } from '../../services/analyticsService';
import type { LeaderboardEntry } from '../../services/analyticsService';

interface AvgDurationByRepChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const AvgDurationByRepChart: React.FC<AvgDurationByRepChartProps> = ({
  businessIdentifier,
  timeRange,
}) => {
  const [data, setData] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasDurationData, setHasDurationData] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await analyticsService.getSalesLeaderboard(businessIdentifier, timeRange);
        const entries = response.leaderboard || [];

        // Check if any entry has avg_duration with actual non-zero value
        const withDuration = entries.filter(
          (entry: any) => entry.avg_duration !== undefined && entry.avg_duration !== null && entry.avg_duration > 0
        );

        if (withDuration.length > 0) {
          setHasDurationData(true);
          const sorted = [...withDuration].sort(
            (a: any, b: any) => (b.avg_duration || 0) - (a.avg_duration || 0)
          );
          setData(sorted);
        } else {
          setHasDurationData(false);
          setData(entries);
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to fetch duration data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (data.length === 0) return {};

    const names = data.map((entry) => entry.salesperson_name);
    const durations = data.map((entry: any) => entry.avg_duration || 0);

    return {
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        formatter: (params: any) => {
          const p = params[0];
          return `${p.name}<br/>Avg Duration: ${p.value.toFixed(1)} min`;
        },
        textStyle: { fontFamily: FONT_FAMILY },
      },
      grid: { top: 30, right: 20, bottom: 60, left: 50, containLabel: true },
      xAxis: {
        type: 'category' as const,
        data: names,
        axisLabel: {
          fontFamily: FONT_FAMILY, fontSize: 11, color: '#718096',
          rotate: names.length > 6 ? 30 : 0, interval: 0,
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value' as const,
        name: 'Duration (min)',
        nameTextStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#A0AEC0' },
        axisLabel: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#718096', formatter: '{value} min' },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#EDF2F7', type: 'dashed' as const } },
      },
      series: [{
        type: 'bar',
        data: durations,
        barMaxWidth: 40,
        label: {
          show: true, position: 'top' as const,
          formatter: (params: any) => `${params.value.toFixed(1)}`,
          fontFamily: FONT_FAMILY, fontSize: 11, color: '#4A5568',
        },
        itemStyle: { color: '#667EEA', borderRadius: [4, 4, 0, 0] },
      }],
    };
  };

  return (
    <ChartCard title="Average Meeting Duration by Rep" isLoading={isLoading} error={error}>
      {hasDurationData && data.length > 0 ? (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '300px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ) : !isLoading && !error ? (
        <Flex direction="column" align="center" justify="center" minH="200px" bg="gray.50" borderRadius="lg" p={6}>
          <Box bg="blue.50" borderRadius="full" p={3} mb={3}>
            <Text fontSize="xl" color="blue.400">&#9202;</Text>
          </Box>
          <Text color="gray.600" fontSize="sm" fontWeight="600" fontFamily={FONT_FAMILY} mb={1}>
            Coming Soon
          </Text>
          <Text color="gray.400" fontSize="xs" fontFamily={FONT_FAMILY} textAlign="center">
            Data will appear once new sessions are processed.
          </Text>
        </Flex>
      ) : null}
    </ChartCard>
  );
};

export default AvgDurationByRepChart;
